// Body for the `retention-refusal` task (Ret-0 "Doctor refused retention").
// Shows, on one page: the DM scans, the retention trigger notes, and the
// doctor's refusal reason. The forward edge (retention-resume-treatment)
// auto-renders below this body via TaskSurface and returns the case to Tx-1.

import { useQuery } from '@tanstack/react-query';
import { HandThumbDownIcon } from '@heroicons/react/24/outline';
import type { TaskBodyProps } from './index';
import { ScansCard } from '../../patient-files/overview/cards/ScansCard';
import { usePatientCard } from '../../../hooks/usePatientCard';
import { fetchTimeline } from '../../../services/timeline';
import { TIMELINE_HISTORY_STALE_MS } from '@odo/shared-ui';
import { ByLine, TriggerNotesCard } from './TriggerNotesCard';

interface Refusal {
    reason: string;
    author: string;
    createdAt: string;
}

export function RetentionRefusalTask({ workCase: c }: TaskBodyProps) {
    const { data: card } = usePatientCard(c.id);

    const {
        data: refusal,
        isLoading: refusalLoading,
        isError: refusalError
    } = useQuery({
        queryKey: ['retention-refusal', c.id],
        queryFn: async (): Promise<Refusal | null> => {
            const res = await fetchTimeline(c.id, {
                type: 'refuse-retention-continue',
                isLatest: true,
                limit: 1
            });
            const event = res.data[0];
            if (!event) return null;
            const reason = (event.payload as { reason?: string } | undefined)?.reason ?? '';
            return {
                reason,
                author: event.createdByUser?.name ?? event.createdBy ?? 'Unknown',
                createdAt: event.createdAt
            };
        },
        staleTime: TIMELINE_HISTORY_STALE_MS,
        enabled: Boolean(c.id)
    });

    return (
        <div className="flex-1 min-h-0 flex gap-4 px-4 pt-3 pb-3 overflow-hidden font-inter">
            {/* Left — DM scans (~55%) */}
            <div className="flex-[55] min-w-0 min-h-0 flex flex-col overflow-hidden [&>*]:flex-1 [&>*]:min-h-0">
                <ScansCard
                    patientId={c.id}
                    monitoring={card?.monitoring ?? undefined}
                    dates={card?.dates ?? undefined}
                />
            </div>

            {/* Right — refusal reason + retention trigger notes (~45%) */}
            <div className="flex-[45] min-w-0 min-h-0 flex flex-col gap-3 overflow-hidden">
                <div className="flex items-center gap-3 px-5 py-3 bg-amber-50 border border-amber-100 rounded-xl flex-shrink-0">
                    <HandThumbDownIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-gray-900">
                            Doctor refused the retention
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5">
                            The patient stays in active treatment. Resume treatment to return the
                            case to monitoring.
                        </div>
                    </div>
                </div>

                {/* Refusal reason */}
                <div className="flex-shrink-0 max-h-[40%] bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
                    <div className="px-5 py-2.5 border-b border-gray-100 flex items-center justify-between gap-2 flex-shrink-0">
                        <span className="font-bold text-sm text-gray-800">Refusal reason</span>
                        {refusal && (
                            <ByLine author={refusal.author} createdAt={refusal.createdAt} />
                        )}
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-5 py-3">
                        {refusalLoading ? (
                            <div className="text-xs text-gray-400 italic">
                                Loading refusal reason…
                            </div>
                        ) : refusalError ? (
                            <div className="text-xs text-red-500">
                                Failed to load the refusal reason.
                            </div>
                        ) : !refusal || !refusal.reason ? (
                            <div className="text-xs text-gray-400 italic">
                                No reason was recorded.
                            </div>
                        ) : (
                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                                {refusal.reason}
                            </p>
                        )}
                    </div>
                </div>

                <TriggerNotesCard patientId={c.id} kind="retention" />
            </div>
        </div>
    );
}
